import React, { useEffect, useState } from 'react';
import { Button } from '@gerdau/hefesto/Button';
import { DateField } from '@gerdau/hefesto-mais-web/DateField';
import { DateRangeType } from '@gerdau/hefesto-mais-web/DateField/DateField.types';
import moment, { isDate } from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import theme from '../../../../assets/styles/theme/theme';
import { FiltersContainer } from '../../../../components/Commons/Facelift/Filters/styles';
import EmitterAndReceiverSelector from '../../../../components/EmitterAndReceiverSelector/Facelift';
import {
  EFilterTagTypes,
  IFilterTag
} from '../../../../components/FiltersTags';
import { ApplicationState } from '../../../../store';
import { ICustomer } from '../../../../store/ducks/customers/types';
import {
  ELongSteelOrdersStatus,
  EOrderTab,
  IBilledOrderData,
  ILongSteelOrderData,
  ILongSteelOrdersState,
  IOpenOrderData,
  IOrdersInitialInputValues
} from '../../../../store/ducks/long-steel-orders/types';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { ECustomerTypeView } from '../../../../store/ducks/user/types';
import { getTaggingInformationScreen } from '../../../../utils/Tagging';
import { DatefieldContainer, OrderFilterBlock } from './styles';

export interface IOrdersStatus {
  id: ELongSteelOrdersStatus;
  description: string;
  ballCollor?: string;
}

interface IOrdersFiltersProps {
  orderTab?: EOrderTab;
  orderState: ILongSteelOrdersState;
  customerViewType: ECustomerTypeView;
  onCleanFilters(): void;
  loadOrders(
    states: string[],
    emitterIds: string[],
    startDate?: string,
    endDate?: string
  ): void;
  isFiltered: boolean;
  setFilteredOrders(filteredOrders: ILongSteelOrderData[]): void;
}

const OrdersFilters: React.FC<IOrdersFiltersProps> = ({
  orderState,
  orderTab,
  customerViewType,
  loadOrders,
  setFilteredOrders
}) => {
  const dispatch = useDispatch();
  const userState = useSelector((state: ApplicationState) => state.user);
  const isUserAdmin = useSelector(
    (state: ApplicationState) => state.user.isManager
  );
  const customers = useSelector((state: ApplicationState) =>
    state.customers.customers.filter((customer) =>
      userState.roles.ordersViewCustomers.includes(customer.customerId)
    )
  );

  const downloadAreaState = useSelector(
    (appState: ApplicationState) => appState.downloadArea
  );

  const [selectedStates, setSelectedStates] = useState<string[]>(
    orderState.initialInputValues.states || []
  );
  const [emitterIds, setEmitterIds] = useState<string[]>(
    orderState.initialInputValues.emitterIds || []
  );
  const [receiverId, setReceiverId] = useState('');

  const [dateSelectedRange, setDateSelectedRange] = useState<DateRangeType>([
    orderState.initialInputValues.startDate
      ? moment(orderState.initialInputValues.startDate).toDate()
      : moment().toDate(),
    orderState.initialInputValues.endDate
      ? moment(orderState.initialInputValues.endDate).toDate()
      : moment().toDate()
  ]);

  const getInitialDateRange = (): DateRangeType => {
    const getDate = (date: string) => {
      return date !== '' ? moment(date, 'YYYY-MM-DD').toDate() : null;
    };
    return [
      getDate(downloadAreaState.startDate),
      getDate(downloadAreaState.endDate)
    ];
  };

  const [dateRange, setDateRange] = useState<DateRangeType>(
    getInitialDateRange()
  );

  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [selectedTab, setSelectedTab] = useState(orderTab);

  const [ordersStatus, setOrdersStatus] = useState<ELongSteelOrdersStatus>(
    ELongSteelOrdersStatus.ALL
  );

  const [alreadyInitialized, setAlreadyInitialized] = useState(false);

  const { screenName = '', itemDescription = '' } =
    getTaggingInformationScreen('long-steel-orders');

  const ordersStatusList: IOrdersStatus[] = [
    {
      id: ELongSteelOrdersStatus.SHIPMENT,
      description: 'Em carregamento',
      ballCollor: theme.colors.blue
    },
    {
      id: ELongSteelOrdersStatus.DELIVERY,
      description: 'Em remessa',
      ballCollor: theme.colors.orange
    },
    {
      id: ELongSteelOrdersStatus.BILLED,
      description: 'Faturado',
      ballCollor: theme.colors.green
    },
    {
      id: ELongSteelOrdersStatus.PENDING,
      description: 'Saldo',
      ballCollor: theme.colors.gray3
    },
    // { id: EOrdersStatus.CANCELLED, description: 'Cancelado', ballCollor: theme.colors.red },
    { id: ELongSteelOrdersStatus.ALL, description: 'Todos' }
  ];

  const [tagsFilter, setTagsFilter] = useState<IFilterTag[]>([]);

  const updateTagsFilter = (
    emitterIds: string[],
    receiverId: string,
    startSelectedDate: moment.Moment,
    endSelectedDate: moment.Moment,
    orderStatus: ELongSteelOrdersStatus,
    selectedDivisions: string[],
    searchInput: string
  ) => {
    const newTagsFilter = tagsFilter.filter((tag) => !tag.canBeRemoved);
    const initialValues = orderState.initialInputValues;

    if (customerViewType === ECustomerTypeView.EMITTER) {
      if (
        receiverId !== '' &&
        orderState.receivers &&
        orderState.receivers.length > 1
      ) {
        const customer = orderState.receivers.find(
          (receiver: { customerId: string }) =>
            receiver.customerId === receiverId
        );
        newTagsFilter.push({
          filterTagType: EFilterTagTypes.RECEIVER,
          filterText: `${customer?.customerId} - ${customer?.name}`,
          canBeRemoved: true
        });
      }
    } else if (
      customerViewType === ECustomerTypeView.RECEIVER ||
      customerViewType === ECustomerTypeView.FABRICATION_RECEIVER
    ) {
      if (
        emitterIds.length > 0 &&
        orderState.emitters &&
        orderState.emitters.length > 1
      ) {
        const customer = orderState.emitters.find(
          (receiver: { customerId: string }) =>
            receiver.customerId === emitterIds[0]
        );
        newTagsFilter.push({
          filterTagType: EFilterTagTypes.EMITTER,
          filterText: `${customer?.customerId} - ${customer?.name}`,
          canBeRemoved: true
        });
      }
    }

    const startDate = startSelectedDate.format('YYYY-MM-DD');
    const endDate = endSelectedDate.format('YYYY-MM-DD');
    if (
      startDate !== initialValues.startDate ||
      endDate !== initialValues.endDate
    ) {
      newTagsFilter.push({
        filterTagType: EFilterTagTypes.DATE,
        filterText: `${startSelectedDate.format(
          'DD/MM/YYYY'
        )} - ${endSelectedDate.format('DD/MM/YYYY')}`,
        canBeRemoved: true
      });
    }

    if (orderStatus !== ELongSteelOrdersStatus.ALL) {
      const statusDescription = ordersStatusList.find(
        (osl) => osl.id === orderStatus
      )?.description;
      newTagsFilter.push({
        filterTagType: EFilterTagTypes.STATUS,
        filterText: statusDescription as string,
        canBeRemoved: true
      });
    }

    if (selectedDivisions.length > 0) {
      selectedDivisions.forEach((division) => {
        const divisionDescription = orderState.divisions.find(
          (div: { division: string }) => div.division === division
        )?.divisionDescription;
        newTagsFilter.push({
          filterTagType: EFilterTagTypes.PRODUCT_TYPE,
          filterText: divisionDescription as string,
          canBeRemoved: true
        });
      });
    }

    if (searchInput !== '') {
      newTagsFilter.push({
        filterTagType: EFilterTagTypes.SEARCH_INPUT,
        filterText: searchInput,
        canBeRemoved: true
      });
    }

    setTagsFilter(newTagsFilter);
  };

  const onChangeStates = (states: string[]) => {
    const selectedCustomers = customers.filter((cust) =>
      emitterIds.includes(cust.customerId)
    );

    if (
      selectedCustomers.some((cust) => !states.includes(cust.locality.state))
    ) {
      setEmitterIds([]);
      setReceiverId('');
    }

    setSelectedStates(states);
  };

  useEffect(() => {
    const setInitialInputValues = (
      initialInputValues: IOrdersInitialInputValues
    ) => {
      setSelectedStates(initialInputValues.states);
      setEmitterIds(initialInputValues.emitterIds);
      setReceiverId(initialInputValues.receiverId);
      setOrdersStatus(initialInputValues.orderStatus);
      setDateRange([
        moment(initialInputValues.startDate).toDate(),
        moment(initialInputValues.endDate).toDate()
      ]);
      setSelectedDivisions([]);
      setSearchInput('');
      setAlreadyInitialized(true);
      initTagsFilter(initialInputValues);
    };

    const initTagsFilter = (initialInputValues: IOrdersInitialInputValues) => {
      const newTagsFilter: IFilterTag[] = [];
      if (initialInputValues.states.length > 0) {
        newTagsFilter.push({
          filterTagType: EFilterTagTypes.STATE,
          filterText: `(${initialInputValues.states.length})`
        });
      }

      const getCustomerNameToTag = (customer: ICustomer): string => {
        const name: string[] = [];
        customer.name.split(' ').forEach((text) => {
          if (name.join(' ').length < 5) {
            name.push(text);
          }
        });
        return name.join(' ');
      };
      if (customerViewType === ECustomerTypeView.EMITTER) {
        if (initialInputValues.emitterIds.length > 0) {
          initialInputValues.emitterIds.forEach((emitterId: string) => {
            const customer = customers.find(
              (cust) => cust.customerId === emitterId
            ) as ICustomer;
            newTagsFilter.push({
              filterTagType: EFilterTagTypes.EMITTER,
              filterText: `${customer.customerId} - ${getCustomerNameToTag(
                customer
              )}`
            });
          });
        }
      } else if (
        customerViewType === ECustomerTypeView.RECEIVER ||
        customerViewType === ECustomerTypeView.FABRICATION_RECEIVER
      ) {
        if (initialInputValues.receiverId !== '') {
          const customer = customers.find(
            (cust) => cust.customerId === initialInputValues.receiverId
          ) as ICustomer;
          newTagsFilter.push({
            filterTagType: EFilterTagTypes.RECEIVER,
            filterText: `${customer.customerId} - ${getCustomerNameToTag(
              customer
            )}`
          });
        }
      }

      setTagsFilter(newTagsFilter);
    };

    if (
      orderState.initialInputValues &&
      !alreadyInitialized &&
      !orderState.loading &&
      orderState.initialized &&
      customers.length > 0
    ) {
      setInitialInputValues(orderState.initialInputValues);
    } else if (orderState.loading) {
      setAlreadyInitialized(false);
    }
  }, [
    orderState.initialInputValues,
    orderState.loading,
    orderState.initialized,
    alreadyInitialized,
    orderTab,
    customers,
    customerViewType
  ]);

  useEffect(() => {
    if (orderTab !== selectedTab) {
      setAlreadyInitialized(false);
      setSelectedTab(orderTab);
    }
  }, [orderTab, selectedTab]);

  const onFilter = (
    states: string[],
    emitterIds: string[],
    receiverId: string,
    startSelectedDate: moment.Moment,
    endSelectedDate: moment.Moment,
    orderStatus: ELongSteelOrdersStatus,
    selectedDivisions: string[],
    searchInput: string
  ) => {
    switch (orderTab) {
      case EOrderTab.OPEN:
        onOpenOrdersFilter(
          states,
          emitterIds,
          receiverId,
          startSelectedDate,
          endSelectedDate,
          orderStatus,
          selectedDivisions,
          searchInput
        );
        break;

      case EOrderTab.BILLED:
        onBilledOrdersFilter(
          states,
          emitterIds,
          receiverId,
          startSelectedDate,
          endSelectedDate,
          selectedDivisions,
          searchInput
        );
        break;

      default:
        break;
    }
  };

  const hasChangedEmitterIds = (): boolean => {
    const initialEmitterIds = orderState?.initialInputValues?.emitterIds || [];
    const hasNewEmitter = emitterIds.some(
      (emitter) => !initialEmitterIds.includes(emitter)
    );

    return hasNewEmitter || emitterIds.length !== initialEmitterIds.length;
  };

  const hasChangedReceiverId = (): boolean => {
    const initialReceiverId = orderState?.initialInputValues?.receiverId || '';

    return receiverId !== '' && receiverId !== initialReceiverId;
  };

  const filterByReceivers = (
    orders: ILongSteelOrderData[]
  ): ILongSteelOrderData[] => {
    return orders.filter((order) => order.shipTo.code === receiverId);
  };

  const filterByEmitters = (
    orders: ILongSteelOrderData[]
  ): ILongSteelOrderData[] => {
    const emitterId = emitterIds[0];
    return orders.filter((order) => order.soldTo.code === emitterId);
  };

  const canLoadOrders = (): boolean => {
    const startSelectedDate = dateRange?.[0];
    const endSelectedDate = dateRange?.[1];
    const startDate = moment(startSelectedDate).format('YYYY-MM-DD');
    const endDate = moment(endSelectedDate).format('YYYY-MM-DD');

    return (
      isUserAdmin ||
      orderState.items.length === 0 ||
      (customerViewType === ECustomerTypeView.EMITTER &&
        hasChangedEmitterIds()) ||
      ((customerViewType === ECustomerTypeView.RECEIVER ||
        customerViewType === ECustomerTypeView.FABRICATION_RECEIVER) &&
        hasChangedReceiverId()) ||
      (orderTab === EOrderTab.BILLED &&
        (startDate !== orderState.initialInputValues?.startDate ||
          endDate !== orderState.initialInputValues?.endDate))
    );
  };

  const onOpenOrdersFilter = (
    states: string[],
    emitterIds: string[],
    receiverId: string,
    startSelectedDate: moment.Moment,
    endSelectedDate: moment.Moment,
    orderStatus: ELongSteelOrdersStatus,
    selectedDivisions: string[],
    searchInput: string
  ) => {
    if (canLoadOrders()) {
      const customerIds: string[] =
        customerViewType === ECustomerTypeView.EMITTER
          ? emitterIds
          : [receiverId];

      loadOrders(states, customerIds);
      return;
    }

    const filterByDate = (orders: IOpenOrderData[]): IOpenOrderData[] => {
      return orders.filter((order) => {
        const orderDate = moment(order.requestedDate, 'YYYY-MM-DD');
        return (
          orderDate.isSameOrAfter(startSelectedDate) &&
          orderDate.isSameOrBefore(endSelectedDate)
        );
      });
    };

    const filterByStatus = (orders: IOpenOrderData[]): IOpenOrderData[] => {
      return orders.filter((order) => {
        switch (orderStatus) {
          case ELongSteelOrdersStatus.PENDING: {
            const pending = order.quantities.pendingQuantity
              ? Number(order.quantities.pendingQuantity)
              : 0;
            return pending && pending > 0;
          }

          case ELongSteelOrdersStatus.DELIVERY: {
            const delivery = order.quantities.deliveryQuantity
              ? Number(order.quantities.deliveryQuantity)
              : 0;
            return delivery && delivery > 0;
          }

          case ELongSteelOrdersStatus.SHIPMENT: {
            const shipment = order.quantities.shipmentQuantity
              ? Number(order.quantities.shipmentQuantity)
              : 0;
            return shipment && shipment > 0;
          }

          case ELongSteelOrdersStatus.BILLED: {
            const billed = order.quantities.billedQuantity
              ? Number(order.quantities.billedQuantity)
              : 0;
            return billed && billed > 0;
          }

          default:
            return true;
        }
      });
    };

    const filterBySearchInput = (
      orders: IOpenOrderData[]
    ): IOpenOrderData[] => {
      return orders.filter((order) => {
        return (
          order.sku.toString().toUpperCase().includes(searchInput) ||
          order.customerSKU.toUpperCase().includes(searchInput) ||
          order.customerOrder.toUpperCase().includes(searchInput) ||
          order.salesOrder.toString().toUpperCase().includes(searchInput) ||
          order.materialDescription.toUpperCase().includes(searchInput)
        );
      });
    };

    const filterByDivision = (orders: IOpenOrderData[]): IOpenOrderData[] => {
      return orders.filter((order) =>
        selectedDivisions.includes(order.division)
      );
    };

    let filtered: IOpenOrderData[] = orderState.items as IOpenOrderData[];

    filtered = filterByDate(filtered);

    if (customerViewType === ECustomerTypeView.EMITTER && receiverId) {
      filtered = filterByReceivers(filtered) as IOpenOrderData[];
    }

    if (
      (customerViewType === ECustomerTypeView.RECEIVER ||
        customerViewType === ECustomerTypeView.FABRICATION_RECEIVER) &&
      emitterIds.length > 0
    ) {
      filtered = filterByEmitters(filtered) as IOpenOrderData[];
    }

    if (searchInput !== '') {
      filtered = filterBySearchInput(filtered);
    }

    if (orderStatus !== ELongSteelOrdersStatus.ALL) {
      filtered = filterByStatus(filtered);
    }

    if (selectedDivisions.length > 0) {
      filtered = filterByDivision(filtered);
    }

    setFilteredOrders(filtered);
    updateTagsFilter(
      emitterIds,
      receiverId,
      startSelectedDate,
      endSelectedDate,
      orderStatus,
      selectedDivisions,
      searchInput
    );
  };

  const onBilledOrdersFilter = (
    states: string[],
    emitterIds: string[],
    receiverId: string,
    startSelectedDate: moment.Moment,
    endSelectedDate: moment.Moment,
    selectedDivisions: string[],
    searchInput: string
  ) => {
    const startDate = startSelectedDate.format('YYYY-MM-DD');
    const endDate = endSelectedDate.format('YYYY-MM-DD');

    if (canLoadOrders()) {
      const customerIds: string[] =
        customerViewType === ECustomerTypeView.EMITTER
          ? emitterIds
          : [receiverId];

      loadOrders(states, customerIds, startDate, endDate);
    } else {
      const filterBySearchInput = (
        orders: IBilledOrderData[]
      ): IBilledOrderData[] => {
        return orders.filter((order) => {
          return (
            order.billingHistory.sku
              .toString()
              .toUpperCase()
              .includes(searchInput) ||
            order.billingHistory.customerSKU
              .toUpperCase()
              .includes(searchInput) ||
            order.billingHistory.salesOrder
              .toString()
              .toUpperCase()
              .includes(searchInput) ||
            order.billingHistory.customerOrder
              .toUpperCase()
              .includes(searchInput) ||
            order.billingHistory.materialDescription
              .toUpperCase()
              .includes(searchInput) ||
            order.invoiceNumber.toUpperCase().includes(searchInput) ||
            order.nfeAccessKey.toUpperCase().includes(searchInput)
          );
        });
      };

      const filterByDivision = (
        orders: IBilledOrderData[]
      ): IBilledOrderData[] => {
        return orders.filter((order) =>
          selectedDivisions.includes(order.billingHistory.division)
        );
      };

      let filtered: IBilledOrderData[] = orderState.items as IBilledOrderData[];

      if (customerViewType === ECustomerTypeView.EMITTER && receiverId) {
        filtered = filterByReceivers(filtered) as IBilledOrderData[];
      }

      if (
        (customerViewType === ECustomerTypeView.RECEIVER ||
          customerViewType === ECustomerTypeView.FABRICATION_RECEIVER) &&
        emitterIds.length > 0
      ) {
        filtered = filterByEmitters(filtered) as IBilledOrderData[];
      }

      if (selectedDivisions.length > 0) {
        filtered = filterByDivision(filtered);
      }

      if (searchInput !== '') {
        filtered = filterBySearchInput(filtered);
      }

      setFilteredOrders(filtered);
      updateTagsFilter(
        emitterIds,
        receiverId,
        startSelectedDate,
        endSelectedDate,
        ELongSteelOrdersStatus.ALL,
        selectedDivisions,
        searchInput
      );
    }
  };

  const getSearchButtonName = (): string => {
    if (orderState.loading) {
      return 'Buscando';
    }

    return 'Buscar';
  };

  const datePickerMaxDate = () => {
    if (dateRange?.[0]) {
      return moment(dateRange?.[0]).add(6, 'months').toDate();
    }
  };

  const datePickerMinDate = () => {
    if (dateRange?.[0]) {
      return moment(dateRange?.[0]).subtract(6, 'months').toDate();
    }

    if (dateRange?.[1]) {
      return moment(dateRange?.[1]).subtract(6, 'months').toDate();
    }
  };

  const handleDateChange = (date: DateRangeType) => {
    if (date && date[0] && date[1]) {
      if (moment(date[0]).isAfter(moment(date[1]))) {
        setDateRange([date[0], null]);
        return;
      }

      if (moment(date[0]).isBefore(moment(date[1]).subtract(6, 'months'))) {
        setDateRange([date[0], null]);
        return;
      }
    }
    setDateRange(date);
  };

  return (
    <>
      <FiltersContainer>
        <OrderFilterBlock>
          <EmitterAndReceiverSelector
            orderOrInvoiceState={orderState}
            customerViewType={customerViewType}
            isUserAdmin={isUserAdmin}
            emitterIds={emitterIds}
            setEmitterIds={setEmitterIds}
            receiverId={receiverId}
            setReceiverId={setReceiverId}
            customers={customers}
            showCustomerStates={true}
            selectedStates={selectedStates}
            onChangeStates={onChangeStates}
            onAdminSearch={(id) => {
              const formattedId = id.padStart(10, '0');
              setEmitterIds([formattedId]);
              loadOrders(selectedStates, [formattedId]);
            }}
          />

          <DatefieldContainer>
            {orderTab === EOrderTab.OPEN && (
              <DateField
                label="Período"
                name="dateFieldOrder"
                onChange={(date) => setDateSelectedRange(date)}
                selectsRange={true}
                range={dateSelectedRange}
                disabled={orderState.items.length === 0}
              />
            )}

            {orderTab === EOrderTab.BILLED && (
              <DateField
                label="Período"
                name="dateFieldBilled"
                helperText="A diferença entre as datas não pode ser maior que 6 meses"
                onChange={handleDateChange}
                selectsRange={true}
                range={dateRange}
                maxDate={datePickerMaxDate()}
                minDate={datePickerMinDate()}
                disabled={orderState.loading}
                allowPartialRange={true}
              />
            )}
          </DatefieldContainer>

          <Button
            variant="primary"
            name="searchButton"
            disabled={orderState.loading || !dateRange?.[1]}
            loading={orderState.loading}
            onClick={() => {
              const startDate = dateRange?.[0];
              const endDate = dateRange?.[1];
              const customerId = emitterIds[0];
              const formatedCustomerId = [];
              userState.isManager && customerId.length < 10
                ? (formatedCustomerId[0] = `0${customerId}`)
                : (formatedCustomerId[0] = customerId);

              onFilter(
                selectedStates,
                userState.isManager ? formatedCustomerId : emitterIds,
                receiverId,
                moment(startDate),
                moment(endDate),
                ordersStatus,
                selectedDivisions,
                searchInput.toUpperCase()
              );

              dispatch(
                sendTagging({
                  screenName,
                  page: document.location.pathname,
                  event: 'click',
                  itemName: 'button:buscar',
                  itemDescription: `${itemDescription}_buscar`,
                  filterInfo: `#est:${
                    selectedStates.length ? selectedStates.join(';') : 'not_set'
                  }#codEmr:${
                    emitterIds.length ? emitterIds.join(';') : 'not_set'
                  }#dtInicio:${moment(startDate).format('YYYY_MM_DD')}#dtFim:${
                    isDate(endDate)
                      ? moment(endDate).format('YYYY_MM_DD')
                      : 'not_set'
                  }`
                })
              );
            }}
          >
            {getSearchButtonName()}
          </Button>
        </OrderFilterBlock>
      </FiltersContainer>
    </>
  );
};

export default OrdersFilters;
